import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faCircle } from '@fortawesome/pro-duotone-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import _ from 'lodash';
import { Observable, Subject } from 'rxjs';
import { buffer, debounceTime, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { WellKnownIconsEnum } from 'src/app/helper/iconHelper';
import { shortcutStringBuilderFromKeyboardEvent } from 'src/app/helper/shortcutStringBuilder';
import { ShortcutTypeGlobal } from 'src/app/samwebDescription/samweb/shortcuts/shortcutTypeGlobal';
import { CloseHotkeyDuplicationDialog, OpenHotkeyDuplicationDialog } from 'src/app/store/samwin/shortcut-behaviour/shortcut-behaviour.actions';
import { ShortcutBehaviourState } from 'src/app/store/samwin/shortcut-behaviour/shortcut-behaviour.state';
import { SettingsChangedHotkeyCombo } from 'src/app/store/settings/settings.actions';
import { SettingsState } from 'src/app/store/settings/settings.state';

@Component({
   selector: 'samweb-hotkey-setting',
   templateUrl: './hotkey-setting.component.html',
   styleUrls: ['./hotkey-setting.component.scss'],
})
export class HotkeySettingComponent implements OnInit, OnDestroy {
   private destroy$ = new Subject();

   @Input()
   public disabled: boolean;

   @Input()
   public shortcutTyp: ShortcutTypeGlobal;

   @Input()
   public searchValue$: Observable<string>;

   public keyEvent$ = new Subject<KeyboardEvent>();

   public hotkeyCombo$: Observable<string>;

   public recordIcon: IconDefinition = faCircle;

   public clearFilterIcon: IconDefinition = WellKnownIconsEnum.CLEAR.iconSmall;

   public isRecording = false;

   public eventShortcutString: string;

   @Select(ShortcutBehaviourState.getHotkeysWithValue)
   public shortCutList$: Observable<string[]>;
   public isDuplicateDialogOpen$: Observable<boolean>;

   constructor(private store: Store, private translateService: TranslateService) {}

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   ngOnInit() {
      this.isDuplicateDialogOpen$ = this.store
         .select(ShortcutBehaviourState.getIsHotkeyDuplicateDialogOpen)
         .pipe(map((IsHotkeyDuplicateDialogOpen) => IsHotkeyDuplicateDialogOpen(this.shortcutTyp)));
      this.hotkeyCombo$ = this.store.select(SettingsState.getHotkeySettingForHotkeyType).pipe(
         map((getHotkeySettingForHotkeyType) => getHotkeySettingForHotkeyType(this.shortcutTyp)),
         map((combo) => (_.isNil(combo) ? this.shortcutTyp.defaultHotkeyCombo : combo)),
         takeUntil(this.destroy$)
      );
      this.shortCutList$
         .pipe(
            switchMap((shortCutList: string[]) =>
               this.keyEvent$.pipe(
                  buffer(this.keyEvent$.pipe(debounceTime(250))),
                  map((eventDatas: KeyboardEvent[]) =>
                     _(
                        _(eventDatas)
                           .map((event) => shortcutStringBuilderFromKeyboardEvent(event))
                           .sortBy((customKeycode) => customKeycode.length)
                           .value()
                     ).last()
                  ),
                  filter((eventShortcutString: string) => eventShortcutString?.length > 0),
                  tap((eventShortcutString: string) => {
                     console.debug('Events: ', eventShortcutString);
                     const currentShortcutCombo = this.store.selectSnapshot(SettingsState.getHotkeySettingForHotkeyType)(this.shortcutTyp);
                     if (currentShortcutCombo === eventShortcutString) {
                        return;
                     }
                     this.eventShortcutString = eventShortcutString;
                     if (shortCutList.includes(this.eventShortcutString) && shortCutList) {
                        this.openDuplicateDialog();
                        return null;
                     }
                     this.store.dispatch(new SettingsChangedHotkeyCombo(this.shortcutTyp, this.eventShortcutString));
                  })
               )
            ),
            takeUntil(this.destroy$)
         )
         .subscribe();
   }

   @HostListener('keydown', ['$event'])
   private onKeyDown(eventData: KeyboardEvent) {
      if (!this.isRecording) {
         return;
      }
      this.keyEvent$.next(eventData);
      eventData.stopPropagation();
      eventData.preventDefault();
   }

   @HostListener('keyup', ['$event'])
   private onKeyUp(eventData: KeyboardEvent) {
      // PrintScreen and Meta key are only triggered on keyup
      if (!this.isRecording || eventData.code !== 'PrintScreen' || !eventData.metaKey) {
         return;
      }
      this.keyEvent$.next(eventData);
      eventData.stopPropagation();
      eventData.preventDefault();
   }

   clearShortcut() {
      const currentShortcutCombo: string = this.store.selectSnapshot(SettingsState.getHotkeySettingForHotkeyType)(this.shortcutTyp);
      if (currentShortcutCombo === '') {
         // reset to default
         this.store.dispatch(new SettingsChangedHotkeyCombo(this.shortcutTyp, this.shortcutTyp.defaultHotkeyCombo));
      } else {
         // remove shortcut
         this.store.dispatch(new SettingsChangedHotkeyCombo(this.shortcutTyp, ''));
      }
   }

   comboChanged(value: any) {
      this.store.dispatch(new SettingsChangedHotkeyCombo(this.shortcutTyp, value));
   }

   stopRecording() {
      this.isRecording = false;
   }

   startRecording() {
      this.isRecording = true;
   }

   openDuplicateDialog() {
      this.store.dispatch(new OpenHotkeyDuplicationDialog(this.shortcutTyp));
   }

   closeDuplicateDialog() {
      this.store.dispatch(new CloseHotkeyDuplicationDialog(this.shortcutTyp));
   }
}
